import NiceModal, { useModal } from "@ebay/nice-modal-react";
import clsx from "clsx";
import { memo, useRef, useState } from "react";
import AnimateHeight from "react-animate-height";
import { FormProvider, useForm } from "react-hook-form";
import ReactModal from "react-modal";

import { ReactComponent as CaretDownIcon } from "assets/icons/caret-down.svg";
import Box from "components/Box";
import Button from "components/Button";
import FilesField from "components/FilesField";
import FileUrl from "components/FileUrl";
import Select from "components/Select";
import SmartEditor from "components/SmartEditor";
import { useReport } from "hooks/apis/question";
import { Question, SmartEditorType } from "types/types";
import {
  questionAnswerTypeOptions,
  questionDifficultyOptions,
} from "utils/const";
import SimpleModal from "./SimpleModal";

type FormValues = {
  title: string;
  responderName: string;
  classificationType: string;
  classificationDifficulty: string;
  files: File[];
};

const ReportPart2 = (question: Question & { report: string }) => {
  const modal = useModal();
  const methods = useForm<FormValues>({
    defaultValues: {
      title: `[답변]${question.title}`,
      responderName: "연구실",
    },
  });
  const [height, setHeight] = useState<0 | "auto">(0);
  const reportMutation = useReport();
  const contentRef = useRef<SmartEditorType>();

  const toggleHeight = () => {
    setHeight((h) => (h ? 0 : "auto"));
  };

  const closeModal = () => {
    modal.hide();
    modal.reject();
  };

  const onSubmit = (form: FormValues) => {
    const content = contentRef.current!.getContent() as string;
    if (!content || content === "<p><br></p>") {
      contentRef.current!.focus();
      return;
    }

    NiceModal.show(SimpleModal, {
      title: "신고/답변 등록",
      message:
        "신고 및 답변 등록 시 수정 및 삭제가 어렵습니다.\n진행 하시겠습니까?",
    })
      .then(() => {
        reportMutation.mutate(
          {
            ...form,
            questionBNo: question.bNo,
            report: question.report,
            content,
          },
          { onSuccess: closeModal },
        );
      })
      .catch(() => {});
  };

  return (
    <ReactModal
      overlayClassName="overflow-auto flex fixed size-full top-0 left-0 z-30"
      className="bg-[rgba(0,0,0,0.2)] min-w-[1280px] 720:min-h-[720px] min-h-full pt-[10.125rem] pb-[3.5rem] h-fit flex justify-center items-start size-full outline-none"
      isOpen={modal.visible}
      onRequestClose={closeModal}
      onAfterClose={() => {
        modal.remove();
      }}
    >
      <FormProvider {...methods}>
        <main className="bg-white flex flex-col w-[70rem] 1200:w-[75rem] 1300:w-[81.25rem] p-[1.875rem] pt-[2.5rem] rounded-md">
          <header className="space-y-[0.625rem]">
            <h2 className="text-center font-bold text-24 leading-[1.375rem]">
              답변 신고
            </h2>
            <p className="text-center text-14">
              <strong className="text-primary font-bold">(2/2)</strong> 답변을
              재입력 해주세요
            </p>
          </header>
          <section className="flex flex-col flex-1 overflow-hidden pt-[1.875rem]">
            <div className="mt-[0.625rem] mb-[0.313rem]">
              <Box>
                <button
                  type="button"
                  onClick={toggleHeight}
                  className="flex justify-between items-center w-full"
                >
                  <h3 className="text-18 font-medium">질문보기</h3>
                  <CaretDownIcon
                    className={clsx("size-[0.875rem]", {
                      "-rotate-180 duration-300": height === 0,
                    })}
                  />
                </button>
              </Box>
              <AnimateHeight height={height} duration={300}>
                <table className="w-full table-fixed border border-gray bg-white mt-[0.313rem]">
                  <tbody>
                    <tr className="h-14 border-b border-gray">
                      <th
                        scope="row"
                        className="border-r border-gray w-[7rem] px-6 text-left bg-lightGray"
                      >
                        질문 유형
                      </th>
                      <td className="px-[1.875rem] w-[7.5rem] 1400:w-[8.125rem] 1500:w-[9.375rem] 1600:w-[10.938rem]">
                        <p className="line-clamp-1">{question.questionType}</p>
                      </td>
                      <th
                        scope="row"
                        className="border-r border-gray w-[8.875rem] px-6 text-left bg-lightGray"
                      >
                        강좌수강유/무
                      </th>
                      <td className="px-[1.875rem] w-[9.375rem] 1600:w-[10.938rem]">
                        <p className="line-clamp-1">
                          {question.courseAttendance}
                        </p>
                      </td>
                      <th
                        scope="row"
                        className="border-r border-gray w-[7rem] px-6 text-left bg-lightGray"
                      >
                        첨부파일
                      </th>
                      <td className="px-[1.875rem]">
                        {!!question.files.length &&
                          question.files.map((file) => (
                            <FileUrl
                              key={file.fileno}
                              file={file}
                              files={question.files}
                            />
                          ))}
                      </td>
                    </tr>
                    <tr className="border-b border-gray">
                      <th
                        scope="row"
                        className="border-r border-gray pt-4 pb-[1.094rem] w-[7rem] pl-6 text-left align-top bg-lightGray"
                      >
                        강좌/강의
                      </th>
                      <td colSpan={5} className="px-[1.875rem] py-4">
                        {question.courseName}{" "}
                        {question.videoLink ? "동영상 위치" : ""}{" "}
                        {question.videoTimestamp && (
                          <a
                            href={question.videoLink}
                            target="_blank noreferrer"
                            className="text-primary"
                          >
                            {question.videoTimestamp}
                          </a>
                        )}
                      </td>
                    </tr>
                    <tr className="border-b border-gray">
                      <th
                        scope="row"
                        className="border-r border-gray pt-4 pb-[1.094rem] w-[7rem] px-6 text-left align-top bg-lightGray"
                      >
                        교재
                      </th>
                      <td colSpan={5} className="px-[1.875rem] py-4">
                        {question.textbookName
                          ? `교재명 :${question.textbookName} 페이지수 :${question.textbookPage} 문제번호 :${question.problemNo ?? "미 기입"}`
                          : ""}
                      </td>
                    </tr>
                    <tr>
                      <th
                        scope="row"
                        className="border-r border-gray pt-4 pb-[1.094rem] w-[7rem] px-6 text-left align-top bg-lightGray"
                      >
                        질문 내용
                      </th>
                      <td
                        colSpan={5}
                        className="px-[1.875rem] py-4 whitespace-pre-wrap"
                      >
                        <div
                          className="leading-normal"
                          dangerouslySetInnerHTML={{
                            __html: question.questionContent || "",
                          }}
                        />
                      </td>
                    </tr>
                  </tbody>
                </table>
              </AnimateHeight>
            </div>
            <Box className="mb-[0.313rem]">
              <h3 className="text-18 font-medium">답변 작성</h3>
            </Box>
            <table className="table-fixed w-full border border-gray">
              <tbody>
                <tr className="border-b border-gray">
                  <th className="w-[7rem] h-14 bg-lightGray px-6 text-left text-16">
                    제목
                  </th>
                  <td className="bg-white pl-[1.875rem] pr-5">
                    <input
                      {...methods.register("title", { required: true })}
                      type="text"
                      className="h-9 w-full rounded-md border border-secondary px-3 outline-none"
                    />
                  </td>
                </tr>
                <tr className="border-b border-gray">
                  <th className="w-[7rem] h-14 bg-lightGray px-6 text-left text-16">
                    답변자
                  </th>
                  <td className="bg-white pl-[1.875rem] pr-5">
                    <input
                      {...methods.register("responderName", { required: true })}
                      type="text"
                      className="h-9 w-full rounded-md border border-secondary px-3 outline-none"
                    />
                  </td>
                </tr>
                <tr className="border-b border-gray">
                  <th className="w-[7rem] h-14 bg-lightGray px-6 text-left text-16">
                    질문 분류
                  </th>
                  <td className="bg-white pl-[1.875rem] pr-5">
                    <div className="flex gap-[0.625rem]">
                      <Select
                        name="classificationType"
                        required
                        options={questionAnswerTypeOptions}
                        border
                        className="!w-[9.375rem]"
                      />
                      <Select
                        name="classificationDifficulty"
                        required
                        options={questionDifficultyOptions}
                        border
                        className="!w-[9.375rem]"
                      />
                    </div>
                  </td>
                </tr>
                <tr className="border-b border-gray">
                  <th className="w-[7rem] align-top pt-[0.938rem] bg-lightGray px-6 text-left text-16">
                    답변 내용
                  </th>
                  <td className="bg-white pl-[1.875rem] pr-5 pt-[0.625rem] pb-6">
                    <SmartEditor ref={contentRef} />
                  </td>
                </tr>
                <tr>
                  <th className="w-[7rem] h-14 bg-lightGray px-6 text-left text-16">
                    첨부파일
                  </th>
                  <td className="bg-white px-[1.875rem]">
                    <FilesField />
                  </td>
                </tr>
              </tbody>
            </table>
            <div className="flex justify-end gap-2 mt-[1.875rem]">
              <Button className="w-[10.688rem]" outline onClick={closeModal}>
                취소
              </Button>
              <Button
                className="w-[10.688rem]"
                onClick={methods.handleSubmit(onSubmit)}
              >
                신고/답변 등록
              </Button>
            </div>
          </section>
        </main>
      </FormProvider>
    </ReactModal>
  );
};

const ReportPart2Modal = NiceModal.create(memo(ReportPart2));
ReportPart2Modal.displayName = "ReportPart2Modal";

export default ReportPart2Modal;
